import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchProductById } from "../actions/ProductActions";
import Product from "../components/Product";
import { ProductDetailSkeleton } from "../components/HomeSkeletons";
import ProductCard from "../components/ProductCard";
import { useImage } from "../contexts/ImageContext";
import formatMoney from "../components/formatMoney";
import { SITE_NAME_FULL } from "../variables";

const ProductView = () => {
  // The 'slug' parameter can be either a slug (preferred) or product_id (backward compatible)
  // The backend will try slug first, then fallback to product_id
  const { slug } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { selectedProduct, loading, error } = useSelector(
    (state) => state.products
  );
  const { getImageUrl } = useImage();

  useEffect(() => {
    dispatch(fetchProductById(slug));
  }, [dispatch, slug]);

  // Prepare meta tag data
  const productUrl = selectedProduct ? window.location.href : "";
  const productImage = selectedProduct
    ? getImageUrl(selectedProduct.image)
    : "";
  const productName = selectedProduct?.name || "Product";

  // Get price
  const productPrice =
    selectedProduct &&
    selectedProduct.discounted_price &&
    parseFloat(selectedProduct.discounted_price) > 0 &&
    parseFloat(selectedProduct.discounted_price) !==
      parseFloat(selectedProduct.base_price)
      ? selectedProduct.discounted_price
      : selectedProduct?.base_price || "0";

  const priceText = selectedProduct ? formatMoney(productPrice) : "";

  // Create description
  let productDescription = "";
  if (selectedProduct?.description) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = selectedProduct.description;
    productDescription = tempDiv.textContent || tempDiv.innerText || "";
    productDescription = productDescription.substring(0, 200).trim();
  }

  if (!productDescription && selectedProduct) {
    productDescription = `${productName} - ${priceText} - Shop now at ${SITE_NAME_FULL}`;
  } else if (selectedProduct) {
    productDescription = `${productDescription} - ${priceText}`;
  }

  // Check if we should scroll to reviews section
  const shouldScrollToReviews = location.hash === "#reviews";

  // Show loader while loading or if product hasn't loaded yet (and no error)
  if (loading || (!selectedProduct && !error)) return <ProductDetailSkeleton />;
  if (error) return <div>Error: {error}</div>;
  if (!selectedProduct) return <div>Product not found</div>;

  return (
    <div>
      {selectedProduct && (
        <Helmet>
          <title>{productName} - {SITE_NAME_FULL}</title>
          <meta name="description" content={productDescription} />

          {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={productUrl} />
          <meta property="og:title" content={productName} />
          <meta property="og:description" content={productDescription} />
          <meta property="og:image" content={productImage} />
          <meta property="og:site_name" content={SITE_NAME_FULL} />

          {/* Product Meta Tags */}
          <meta property="product:price:amount" content={productPrice} />
          <meta property="product:price:currency" content="RWF" />
          {selectedProduct.brand && (
            <meta property="product:brand" content={selectedProduct.brand} />
          )}

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={productUrl} />
          <meta name="twitter:title" content={productName} />
          <meta name="twitter:description" content={productDescription} />
          <meta name="twitter:image" content={productImage} />
        </Helmet>
      )}

      <Product
        product={selectedProduct}
        scrollToReviews={shouldScrollToReviews}
      />
      <div className="related_products" id="related_products">
        <h2>Related Products</h2>
        <div className="related_products_container">
          {selectedProduct.related_products.map((product) => (
            <ProductCard product={product} key={product.product_id} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductView;
